/**
 * LeaderReviewPage.tsx
 * Main entry for the unified Leader EPR Review module.
 *
 * Admin view  — session management: create, enroll leaders, advance stages
 * Leader view — personal journey: KR context card, stage status, CTAs
 * Both roles  — admin is also a participant, so they see both views
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchSessions, fetchActiveSession, fetchEnrollments,
  createSession, updateSession, advanceSessionStatus, deleteSession,
  enrollLeader, removeEnrollment, syncPeerCounts, refreshEnrollmentKRs,
  computeKRInsight, fetchLeaderUsers, checkAndAutoAdvance,
  fetchTemplates, createTemplate, updateTemplate, deleteTemplate,
  type LeaderUser,
} from '../services/leaderReviewService';
import { fetchKRsForReview } from '../services/evidenceService';
import type {
  LeaderReviewSession, LeaderReviewEnrollment, LeaderReviewStatus,
  LeaderReviewScoreWeights, KRProgressEntry, CyclePeriod,
  LeaderReviewTemplate, LeaderMuscle,
} from '../types';
import { LEADER_REVIEW_STATUS_LABELS, LEADER_REVIEW_STAGES, LEADER_MUSCLES } from '../types';

// ─── helpers ─────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<LeaderReviewStatus, string> = {
  setup:         'bg-gray-100 text-gray-600',
  self_review:   'bg-amber-50 text-amber-700 border border-amber-200',
  peer_review:   'bg-blue-50 text-blue-700 border border-blue-200',
  live:          'bg-purple-50 text-purple-700 border border-purple-200',
  consolidation: 'bg-orange-50 text-orange-700 border border-orange-200',
  completed:     'bg-emerald-50 text-emerald-700 border border-emerald-200',
};

const NEXT_STATUS: Partial<Record<LeaderReviewStatus, LeaderReviewStatus>> = {
  setup:         'self_review',
  self_review:   'peer_review',
  peer_review:   'live',
  live:          'consolidation',
  consolidation: 'completed',
};

const NEXT_LABEL: Partial<Record<LeaderReviewStatus, string>> = {
  setup:         'Open Self-Review',
  self_review:   'Close Self-Review & Open Peer Review',
  peer_review:   'Close Peer Review & Start Live Session',
  live:          'End Live Session → Consolidation',
  consolidation: 'Publish Results',
};

function krStats(krs: KRProgressEntry[]) {
  const achieved    = krs.filter(k => (k.progress ?? 0) >= 100 || (k.krStatus ?? '').toLowerCase().includes('achiev')).length;
  const inProgress  = krs.filter(k => (k.progress ?? 0) > 0 && (k.progress ?? 0) < 100 && !(k.krStatus ?? '').toLowerCase().includes('achiev')).length;
  const notStarted  = krs.length - achieved - inProgress;
  const pct         = krs.length ? Math.round((achieved / krs.length) * 100) : 0;
  return { achieved, inProgress, notStarted, pct };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface KRContextCardProps { krs: KRProgressEntry[]; loading: boolean }

function KRContextCard({ krs, loading }: KRContextCardProps) {
  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
      <div className="h-4 bg-gray-100 rounded w-1/3 mb-3" />
      <div className="h-2 bg-gray-100 rounded w-full mb-2" />
      <div className="h-3 bg-gray-100 rounded w-2/3" />
    </div>
  );

  const { achieved, inProgress, notStarted, pct } = krStats(krs);
  const insight = computeKRInsight(krs);

  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400';
  const pctColor = pct >= 80 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500';

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">
        Your H1 Snapshot — from OKR Tracker
      </p>

      {krs.length === 0 ? (
        <p className="text-sm text-gray-500">No KRs synced. Check your OKR Tracker assignment.</p>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-3">
            <span className={`text-2xl font-bold ${pctColor}`}>{pct}%</span>
            <div className="flex-1">
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap mb-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{achieved} Achieved
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />{inProgress} In Progress
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />{notStarted} Not Started
            </span>
          </div>

          <p className="text-xs text-gray-500 italic leading-relaxed border-t border-gray-100 pt-3">
            "{insight}"
          </p>
        </>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const LeaderReviewPage: React.FC = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const isHR = ['superadmin', 'admin', 'hr_admin'].includes(user?.role ?? '');

  // Session state
  const [sessions, setSessions]           = useState<LeaderReviewSession[]>([]);
  const [activeSession, setActiveSession] = useState<LeaderReviewSession | null>(null);
  const [enrollments, setEnrollments]     = useState<LeaderReviewEnrollment[]>([]);
  const [myEnrollment, setMyEnrollment]   = useState<LeaderReviewEnrollment | null>(null);
  const [myKRs, setMyKRs]                 = useState<KRProgressEntry[]>([]);
  const [loadingKRs, setLoadingKRs]       = useState(true);
  const [loading, setLoading]             = useState(true);
  const [advancing, setAdvancing]         = useState(false);
  const [savingFacilitator, setSavingFacilitator] = useState(false);

  // Admin tabs
  const [adminTab, setAdminTab] = useState<'sessions' | 'templates'>('sessions');

  // Admin session creation modal
  const [showCreateModal, setShowCreateModal]   = useState(false);
  const [showEnrollModal, setShowEnrollModal]   = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  // Create form state
  const [form, setForm] = useState({
    name: `H1 ${new Date().getFullYear()} Leader EPR`,
    period: 'H1' as CyclePeriod,
    year: new Date().getFullYear(),
    selfReviewStartDate: '',
    selfReviewDeadline: '',
    peerReviewDeadline: '',
    liveSessionDate: '',
    scoreWeights: { peer: 0.5, self: 0.3, live: 0.2 } as LeaderReviewScoreWeights,
    templateId: '' as string,
  });

  // Template management
  const [templates, setTemplates]               = useState<LeaderReviewTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate]   = useState<LeaderReviewTemplate | null>(null);
  const [tmplName, setTmplName]                 = useState('');
  const [tmplDesc, setTmplDesc]                 = useState('');
  const [tmplMuscles, setTmplMuscles]           = useState<LeaderMuscle[]>(LEADER_MUSCLES.map(m => ({ ...m })));
  const [savingTemplate, setSavingTemplate]     = useState(false);

  // Enroll modal
  const [leaderUsers, setLeaderUsers]       = useState<LeaderUser[]>([]);
  const [leaderUsersLoading, setLeaderUsersLoading] = useState(false);
  const [selectedUids, setSelectedUids]     = useState<Set<string>>(new Set());
  const [enrolling, setEnrolling]           = useState(false);

  const viewSession = selectedSessionId
    ? sessions.find(s => s.id === selectedSessionId) ?? activeSession
    : activeSession;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, active] = await Promise.all([fetchSessions(), fetchActiveSession()]);
      let currentActive = active;

      // Auto-advance any eligible sessions (date-based or completion-based)
      for (const s of all) {
        if (['setup', 'self_review', 'peer_review'].includes(s.status)) {
          const enrolls = await fetchEnrollments(s.id);
          const advanced = await checkAndAutoAdvance(s, enrolls);
          if (advanced && s.id === active?.id) {
            // Refresh the active session object with the new status
            currentActive = { ...s, status: advanced };
          }
        }
      }

      const refreshedAll = await fetchSessions();
      setSessions(refreshedAll);
      setActiveSession(currentActive);
      if (currentActive) {
        // Sync peer counts here (admin context) so all enrollment totalPeerCount values stay current
        if (isHR) await syncPeerCounts(currentActive.id).catch(() => {});
        const enrolls = await fetchEnrollments(currentActive.id);
        setEnrollments(enrolls);
        setMyEnrollment(enrolls.find(e => e.leaderId === user?.uid) ?? null);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => { load(); }, [load]);

  // Reload enrollments whenever the expanded session changes
  useEffect(() => {
    const id = viewSession?.id;
    if (!id) return;
    fetchEnrollments(id).then(enrolls => {
      setEnrollments(enrolls);
      setMyEnrollment(enrolls.find(e => e.leaderId === user?.uid) ?? null);
    });
  }, [viewSession?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user?.uid) return;
    setLoadingKRs(true);
    fetchKRsForReview(user.uid)
      .then(setMyKRs)
      .catch(() => setMyKRs([]))
      .finally(() => setLoadingKRs(false));
  }, [user?.uid]);

  // Load templates when HR opens template tab
  useEffect(() => {
    if (isHR && adminTab === 'templates' && templates.length === 0) loadTemplates();
  }, [isHR, adminTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also pre-load templates for the create-session dropdown
  useEffect(() => {
    if (isHR && showCreateModal && templates.length === 0) loadTemplates();
  }, [isHR, showCreateModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Session creation ───────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!user?.uid) return;
    const id = await createSession({
      ...form,
      status: 'setup',
      createdBy: user.uid,
    });
    setShowCreateModal(false);
    await load();
    setSelectedSessionId(id);
  };

  // ── Enroll leader ──────────────────────────────────────────────────────────

  const openEnrollModal = async () => {
    setShowEnrollModal(true);
    setLeaderUsersLoading(true);
    try {
      const users = await fetchLeaderUsers();
      setLeaderUsers(users);
    } finally {
      setLeaderUsersLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!viewSession || selectedUids.size === 0) return;
    setEnrolling(true);
    try {
      // Always fetch fresh for the correct session — avoids cross-session stale data
      const currentEnrolls = await fetchEnrollments(viewSession.id);
      const alreadyEnrolled = new Set(currentEnrolls.map(e => e.leaderId));
      const toEnroll = leaderUsers.filter(u => selectedUids.has(u.uid) && !alreadyEnrolled.has(u.uid));
      if (toEnroll.length === 0) {
        alert('All selected leaders are already enrolled in this session.');
        setSelectedUids(new Set());
        setShowEnrollModal(false);
        return;
      }
      await Promise.all(
        toEnroll.map(u =>
          enrollLeader(viewSession.id, {
            leaderId: u.uid,
            leaderName: u.name,
            leaderEmail: u.email,
            leaderJobTitle: u.jobTitle ?? '',
          }),
        ),
      );
      await syncPeerCounts(viewSession.id);
      setSelectedUids(new Set());
      setShowEnrollModal(false);
      // If session is still in setup, auto-advance to self_review so leaders can start immediately
      if (viewSession.status === 'setup') {
        await advanceSessionStatus(viewSession.id, 'self_review');
      }
      await load();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      alert(`Enroll failed: ${msg}`);
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemoveEnrollment = async (enrollmentId: string, leaderName: string) => {
    if (!viewSession) return;
    if (!window.confirm(`Remove ${leaderName} from this session? Their submitted assessments will remain but they will no longer appear in the review list.`)) return;
    await removeEnrollment(enrollmentId);
    await syncPeerCounts(viewSession.id);
    setEnrollments(await fetchEnrollments(viewSession.id));
  };

  const handleRefreshKRs = async (e: LeaderReviewEnrollment) => {
    await refreshEnrollmentKRs(e.id, e.leaderId);
    setEnrollments(await fetchEnrollments(viewSession!.id));
  };

  // ── Advance stage ──────────────────────────────────────────────────────────

  const handleAdvance = async () => {
    if (!viewSession) return;
    const next = NEXT_STATUS[viewSession.status];
    if (!next) return;
    setAdvancing(true);
    try {
      await advanceSessionStatus(viewSession.id, next);
      await load();
    } finally {
      setAdvancing(false);
    }
  };

  // ── Delete session ─────────────────────────────────────────────────────────

  const handleDeleteSession = async (sessionId: string, sessionName: string) => {
    if (!window.confirm(`Delete "${sessionName}"?\n\nThis will permanently remove the session header. Enrolled leaders, assessments and ratings will remain in Firestore but will no longer be linked. This cannot be undone.`)) return;
    try {
      await deleteSession(sessionId);
      if (selectedSessionId === sessionId) setSelectedSessionId(null);
      await load();
    } catch (err: unknown) {
      alert(`Delete failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // ── Facilitator assignment ─────────────────────────────────────────────────

  const handleSetFacilitator = async (sessionId: string, leaderId: string, leaderName: string) => {
    setSavingFacilitator(true);
    try {
      await updateSession(sessionId, { facilitatorId: leaderId, facilitatorName: leaderName });
      setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, facilitatorId: leaderId, facilitatorName: leaderName } : s));
    } finally {
      setSavingFacilitator(false);
    }
  };

  // ── Template management ────────────────────────────────────────────────────

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try { setTemplates(await fetchTemplates()); }
    finally { setTemplatesLoading(false); }
  };

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTmplName('');
    setTmplDesc('');
    setTmplMuscles(LEADER_MUSCLES.map(m => ({ ...m })));
    setShowTemplateModal(true);
  };

  const openEditTemplate = (t: LeaderReviewTemplate) => {
    setEditingTemplate(t);
    setTmplName(t.name);
    setTmplDesc(t.description ?? '');
    setTmplMuscles(t.muscles.map(m => ({ ...m })));
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!user?.uid || !tmplName.trim()) return;
    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, { name: tmplName, description: tmplDesc, muscles: tmplMuscles });
      } else {
        await createTemplate({ name: tmplName, description: tmplDesc, muscles: tmplMuscles, createdBy: user.uid });
      }
      setShowTemplateModal(false);
      await loadTemplates();
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    await deleteTemplate(id);
    await loadTemplates();
  };

  const updateMuscleField = (idx: number, field: keyof LeaderMuscle, value: string) => {
    setTmplMuscles(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const updateRubric = (idx: number, score: number, value: string) => {
    setTmplMuscles(prev => prev.map((m, i) =>
      i === idx ? { ...m, rubric: { ...m.rubric, [score]: value } } : m,
    ));
  };

  // ── Score weight helpers ───────────────────────────────────────────────────

  const weightsValid = Math.abs(form.scoreWeights.peer + form.scoreWeights.self + form.scoreWeights.live - 1) < 0.01;

  // ── Stage CTA for leader ───────────────────────────────────────────────────

  function leaderCTA() {
    if (!activeSession || !myEnrollment) return null;
    switch (activeSession.status) {
      case 'setup':
        return { label: 'Waiting for admin to open self-review…', disabled: true, path: null, variant: 'waiting' as const };
      case 'self_review':
        return myEnrollment.selfSubmitted
          ? { label: '✓ Self-Review Submitted', disabled: true, path: null, variant: 'done' as const }
          : { label: 'Start Self-Review →', disabled: false, path: `/leader-review/self/${activeSession.id}`, variant: 'action' as const };
      case 'peer_review':
        if (myEnrollment.totalPeerCount === 0)
          return { label: 'No other leaders enrolled yet — check back soon', disabled: true, path: null, variant: 'waiting' as const };
        return myEnrollment.peerSubmittedCount >= myEnrollment.totalPeerCount
          ? { label: '✓ Peer Reviews Done', disabled: true, path: null, variant: 'done' as const }
          : { label: `Rate Peers (${myEnrollment.peerSubmittedCount}/${myEnrollment.totalPeerCount} done) →`, disabled: false, path: `/leader-review/peers/${activeSession.id}`, variant: 'action' as const };
      case 'live':
        return { label: 'Join Live Session →', disabled: false, path: `/leader-review/live/${activeSession.id}`, variant: 'action' as const };
      case 'consolidation':
        return { label: 'Live session complete — results being compiled', disabled: true, path: null, variant: 'waiting' as const };
      case 'completed':
        return { label: 'View Your Results →', disabled: false, path: `/leader-review/results/${activeSession.id}`, variant: 'action' as const };
      default:
        return null;
    }
  }

  const cta = leaderCTA();

  // ─────────────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-5 h-5 border-2 border-[#011132] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Leader EPR Review</h1>
          <p className="text-sm text-gray-500 mt-0.5">Behavioral + delivery review for the leadership team</p>
        </div>
        {isHR && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 bg-[#011132] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#0a2254] transition-colors"
          >
            <span className="material-symbols-rounded" style={{ fontSize: 16 }}>add</span>
            New Session
          </button>
        )}
      </div>

      {/* ── Leader personal journey — only if enrolled ── */}
      {activeSession && (myEnrollment || isHR) && (
        <section className="space-y-4">
          {/* Session banner */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-base font-bold text-gray-900">{activeSession.name}</h2>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[activeSession.status]}`}>
                    {LEADER_REVIEW_STATUS_LABELS[activeSession.status]}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Self-review deadline: <span className="font-semibold text-gray-700">{activeSession.selfReviewDeadline || '—'}</span>
                  {' · '}
                  Peer review deadline: <span className="font-semibold text-gray-700">{activeSession.peerReviewDeadline || '—'}</span>
                  {' · '}
                  Live session: <span className="font-semibold text-gray-700">{activeSession.liveSessionDate || '—'}</span>
                </p>
              </div>
              {activeSession.status === 'completed' && (
                <button
                  onClick={() => navigate(`/leader-review/results/${activeSession.id}`)}
                  className="text-sm font-medium text-[#011132] border border-[#011132] px-3 py-1.5 rounded-lg hover:bg-[#011132] hover:text-white transition-colors"
                >
                  View Results →
                </button>
              )}
            </div>

            {/* Stage progress bar */}
            <div className="flex items-center gap-1">
              {LEADER_REVIEW_STAGES.filter(s => s !== 'setup').map((stage, i) => {
                const stageIndex   = LEADER_REVIEW_STAGES.indexOf(stage);
                const currentIndex = LEADER_REVIEW_STAGES.indexOf(activeSession.status);
                const done    = stageIndex < currentIndex;
                const current = stageIndex === currentIndex;
                return (
                  <React.Fragment key={stage}>
                    <div className="flex flex-col items-center gap-1 flex-1">
                      <div className={`w-full h-1.5 rounded-full ${done ? 'bg-[#011132]' : current ? 'bg-blue-400' : 'bg-gray-100'}`} />
                      <span className={`text-[10px] font-medium ${current ? 'text-blue-600' : done ? 'text-gray-700' : 'text-gray-500'}`}>
                        {LEADER_REVIEW_STATUS_LABELS[stage]}
                      </span>
                    </div>
                    {i < LEADER_REVIEW_STAGES.filter(s => s !== 'setup').length - 1 && <div className="w-1" />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* ── Action nudge banner — only when there's something to do ── */}
          {cta && !cta.disabled && myEnrollment && (() => {
            const isPeer = activeSession.status === 'peer_review';
            const remaining = (myEnrollment.totalPeerCount ?? 0) - (myEnrollment.peerSubmittedCount ?? 0);
            return (
              <div className={`rounded-xl p-4 border flex items-center justify-between gap-3 ${isPeer ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isPeer ? 'bg-blue-100' : 'bg-amber-100'}`}>
                    <span className={`material-symbols-rounded ${isPeer ? 'text-blue-600' : 'text-amber-600'}`} style={{ fontSize: 18 }}>
                      {isPeer ? 'group' : 'edit_note'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-bold ${isPeer ? 'text-blue-800' : 'text-amber-800'}`}>
                      {isPeer
                        ? `Peer Review is open${remaining > 0 ? ` — ${remaining} peer${remaining !== 1 ? 's' : ''} left to rate` : ''}`
                        : 'Self Review is open — share your self-assessment'
                      }
                    </p>
                    <p className={`text-xs ${isPeer ? 'text-blue-600' : 'text-amber-600'}`}>
                      Deadline: {isPeer ? (activeSession.peerReviewDeadline || '—') : (activeSession.selfReviewDeadline || '—')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => cta.path && navigate(cta.path)}
                  className={`text-sm font-semibold px-4 py-2 rounded-lg flex-shrink-0 transition-colors ${isPeer ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-amber-500 text-white hover:bg-amber-600'}`}
                >
                  {isPeer ? 'Rate Peers →' : 'Start Now →'}
                </button>
              </div>
            );
          })()}

          {/* KR context card + stage action */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <KRContextCard krs={myKRs} loading={loadingKRs} />

            {/* My stage status */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">My Progress</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Self-Assessment</span>
                    {myEnrollment?.selfSubmitted
                      ? <span className="text-emerald-600 font-medium flex items-center gap-1"><span className="material-symbols-rounded" style={{ fontSize: 14 }}>check_circle</span>Done</span>
                      : activeSession.status === 'setup'
                      ? <span className="text-gray-500">Not open yet</span>
                      : <span className="text-amber-600 font-semibold">Pending</span>
                    }
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Peer Reviews</span>
                    {!myEnrollment || activeSession.status === 'setup' || activeSession.status === 'self_review' ? (
                      <span className="text-gray-500">—</span>
                    ) : myEnrollment.peerSubmittedCount >= myEnrollment.totalPeerCount && myEnrollment.totalPeerCount > 0 ? (
                      <span className="text-emerald-600 font-medium">{myEnrollment.peerSubmittedCount}/{myEnrollment.totalPeerCount} Done</span>
                    ) : (
                      <span className="text-gray-600 font-medium">{myEnrollment.peerSubmittedCount} / {myEnrollment.totalPeerCount}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Results</span>
                    {activeSession.status === 'completed'
                      ? <span className="text-emerald-600 font-medium">Published</span>
                      : <span className="text-gray-500">After live session</span>
                    }
                  </div>
                </div>
              </div>

              {cta && !cta.disabled && (
                <button
                  onClick={() => cta.path && navigate(cta.path)}
                  className="mt-4 w-full bg-[#011132] text-white text-sm font-medium py-2.5 rounded-lg hover:bg-[#0a2254] transition-colors"
                >
                  {cta.label}
                </button>
              )}
              {cta?.disabled && cta.variant === 'done' && (
                <div className="mt-4 w-full text-center text-sm text-emerald-600 font-medium py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                  {cta.label}
                </div>
              )}
              {cta?.disabled && cta.variant === 'waiting' && (
                <div className="mt-4 space-y-2">
                  <div className="w-full text-center text-sm text-amber-700 font-medium py-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
                    {cta.label}
                  </div>
                  {activeSession.status === 'setup' && (
                    <p className="text-xs text-gray-500 text-center leading-relaxed">
                      Once your admin opens the session, you'll be able to complete your self-assessment, rate your peers, and join the live review.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {!activeSession && !isHR && (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <span className="material-symbols-rounded text-4xl text-gray-400">pending</span>
          <p className="text-gray-500 mt-3 text-sm">No active Leader EPR session right now.</p>
          <p className="text-gray-400 text-xs mt-1">Your admin will open the next session.</p>
        </div>
      )}

      {/* Enrolled but not yet in active session — enrolled in setup */}
      {activeSession && !myEnrollment && !isHR && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <span className="material-symbols-rounded text-3xl text-amber-400">info</span>
          <p className="text-amber-800 font-medium mt-2 text-sm">You are not yet enrolled in this session.</p>
          <p className="text-amber-600 text-xs mt-1">Ask your admin to enroll you in {activeSession.name}.</p>
        </div>
      )}

      {/* ── Admin: tabs ── */}
      {isHR && (
        <section className="space-y-4">
          {/* Tab switcher */}
          <div className="flex items-center gap-1 border-b border-gray-200">
            {(['sessions', 'templates'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setAdminTab(tab)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors capitalize ${
                  adminTab === tab
                    ? 'border-[#011132] text-[#011132]'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                {tab === 'sessions' ? 'Sessions' : 'Muscle Templates'}
              </button>
            ))}
          </div>

          {/* ── TEMPLATE TAB ── */}
          {adminTab === 'templates' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">Customise the 8 Leadership Muscles — names, anchors, rubric — per review cycle.</p>
                <button
                  onClick={openNewTemplate}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#011132] border border-[#011132]/30 px-3 py-1.5 rounded-lg hover:bg-[#011132]/5 transition-colors"
                >
                  <span className="material-symbols-rounded" style={{ fontSize: 15 }}>add</span>
                  New Template
                </button>
              </div>

              {/* Default template card */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-gray-800">Default — H1 2026 Leadership Manifesto</p>
                    <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Built-in</span>
                  </div>
                  <p className="text-xs text-gray-500">8 muscles · Scoring anchored to behaviour & impact · Consistency and fairness across leaders and sites</p>
                </div>
                <button
                  onClick={() => {
                    setEditingTemplate(null);
                    setTmplName('Copy of Default');
                    setTmplDesc('Based on H1 2026 Leadership Manifesto');
                    setTmplMuscles(LEADER_MUSCLES.map(m => ({ ...m })));
                    setShowTemplateModal(true);
                  }}
                  className="text-xs text-gray-500 hover:text-[#011132] border border-gray-200 px-2.5 py-1 rounded-lg transition-colors flex-shrink-0"
                >
                  Duplicate & Edit
                </button>
              </div>

              {/* Custom templates */}
              {templatesLoading ? (
                <div className="text-xs text-gray-500 text-center py-4 animate-pulse">Loading templates…</div>
              ) : templates.map(t => (
                <div key={t.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.description || `${t.muscles.length} muscles`}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => openEditTemplate(t)} className="text-xs text-[#011132] border border-[#011132]/30 px-2.5 py-1 rounded-lg hover:bg-[#011132]/5 transition-colors">Edit</button>
                    <button onClick={() => handleDeleteTemplate(t.id)} className="text-xs text-red-400 border border-red-200 px-2.5 py-1 rounded-lg hover:bg-red-50 transition-colors">Delete</button>
                  </div>
                </div>
              ))}
              {!templatesLoading && templates.length === 0 && (
                <p className="text-xs text-gray-500 text-center py-4">No custom templates yet. The default template is always available.</p>
              )}
            </div>
          )}

          {/* ── SESSIONS TAB (original content) ── */}
          {adminTab === 'sessions' && <div className="space-y-3">
          <p className="sr-only">Session Management</p>

          {sessions.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <span className="material-symbols-rounded text-4xl text-gray-400">add_circle</span>
              <p className="text-gray-500 mt-2 text-sm">No sessions yet. Create the first Leader EPR session.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map(s => (
                <div
                  key={s.id}
                  className={`bg-white border rounded-xl overflow-hidden cursor-pointer transition-all ${selectedSessionId === s.id ? 'border-[#011132] ring-1 ring-[#011132]/20' : 'border-gray-200 hover:border-gray-300'}`}
                  onClick={() => setSelectedSessionId(prev => prev === s.id ? null : s.id)}
                >
                  <div className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status]}`}>
                        {LEADER_REVIEW_STATUS_LABELS[s.status]}
                      </span>
                      <span className="font-semibold text-gray-900 text-sm">{s.name}</span>
                      <span className="text-xs text-gray-500">{s.period} {s.year}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={e => { e.stopPropagation(); handleDeleteSession(s.id, s.name); }}
                        title="Delete session"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <span className="material-symbols-rounded" style={{ fontSize: 16 }}>delete</span>
                      </button>
                      <span className="material-symbols-rounded text-gray-400" style={{ fontSize: 18 }}>
                        {selectedSessionId === s.id ? 'expand_less' : 'expand_more'}
                      </span>
                    </div>
                  </div>

                  {selectedSessionId === s.id && (
                    <div className="border-t border-gray-100 p-5 space-y-5" onClick={e => e.stopPropagation()}>

                      {/* Setup: no-leaders warning */}
                      {s.status === 'setup' && enrollments.filter(e => e.sessionId === s.id).length === 0 && (
                        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                          <span className="material-symbols-rounded text-blue-500 mt-0.5 flex-shrink-0" style={{ fontSize: 18 }}>info</span>
                          <p className="text-sm text-blue-800">
                            Enroll leaders first — self-review will <strong>open automatically</strong> once you confirm enrollment.
                          </p>
                        </div>
                      )}

                      {/* Stage advance — hidden for setup (auto-triggers on enroll) */}
                      {s.status !== 'completed' && s.status !== 'setup' && (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={handleAdvance}
                            disabled={advancing}
                            className="inline-flex items-center gap-2 bg-[#011132] text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-[#0a2254] transition-colors"
                          >
                            {advancing ? 'Advancing…' : (NEXT_LABEL[s.status] ?? 'Advance Stage')}
                          </button>
                          {s.status === 'live' && (
                            <button
                              onClick={() => navigate(`/leader-review/live/${s.id}`)}
                              className="text-sm font-medium text-purple-700 border border-purple-300 px-4 py-2 rounded-lg hover:bg-purple-50 transition-colors"
                            >
                              Open Live Session →
                            </button>
                          )}
                          {s.status === 'consolidation' && (
                            <button
                              onClick={() => navigate(`/leader-review/results/${s.id}`)}
                              className="text-sm font-medium text-orange-700 border border-orange-300 px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors"
                            >
                              Open Consolidation →
                            </button>
                          )}
                        </div>
                      )}

                      {/* Auto-schedule timeline */}
                      {(s.selfReviewStartDate || s.selfReviewDeadline || s.peerReviewDeadline) && (
                        <div>
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                            Auto Schedule
                            <span className="ml-2 text-[10px] text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full normal-case font-medium">auto-advance on</span>
                          </p>
                          <div className="flex items-start gap-0 flex-wrap">
                            {[
                              { label: 'Opens self-review', date: s.selfReviewStartDate, auto: true },
                              { label: 'Self-review closes', date: s.selfReviewDeadline, auto: true },
                              { label: 'Peer review closes', date: s.peerReviewDeadline, auto: true },
                              { label: 'Live session', date: s.liveSessionDate, auto: false },
                            ].filter(x => x.date).map((x, i, arr) => {
                              const today = new Date().toISOString().split('T')[0];
                              const past = x.date! < today;
                              return (
                                <React.Fragment key={x.label}>
                                  <div className="flex flex-col items-center">
                                    <div className={`w-2.5 h-2.5 rounded-full border-2 ${past ? 'bg-gray-300 border-gray-300' : x.auto ? 'bg-blue-400 border-blue-400' : 'bg-gray-400 border-gray-400'}`} />
                                    <p className={`text-[10px] mt-1 text-center max-w-[72px] font-medium ${past ? 'text-gray-400' : 'text-gray-700'}`}>{x.date}</p>
                                    <p className={`text-[10px] text-center max-w-[72px] ${past ? 'text-gray-400' : 'text-gray-500'}`}>{x.label}</p>
                                  </div>
                                  {i < arr.length - 1 && <div className={`flex-1 h-px mt-[5px] min-w-[12px] ${past ? 'bg-gray-200' : 'bg-blue-200'}`} />}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Score weights display */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Score Formula</p>
                        <div className="flex items-center gap-4 flex-wrap text-sm">
                          <span className="text-gray-700">Peer <strong>{Math.round(s.scoreWeights.peer * 100)}%</strong></span>
                          <span className="text-gray-500 font-bold">+</span>
                          <span className="text-gray-700">Self <strong>{Math.round(s.scoreWeights.self * 100)}%</strong></span>
                          <span className="text-gray-500 font-bold">+</span>
                          <span className="text-gray-700">Live <strong>{Math.round(s.scoreWeights.live * 100)}%</strong></span>
                        </div>
                      </div>

                      {/* Facilitator assignment */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Live Session Facilitator</p>
                        {enrollments.filter(e => e.sessionId === s.id).length === 0 ? (
                          <p className="text-xs text-gray-500">Enroll leaders first to assign a facilitator.</p>
                        ) : (
                          <div className="flex items-center gap-2">
                            <select
                              className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                              value={s.facilitatorId ?? ''}
                              disabled={savingFacilitator}
                              onChange={e => {
                                const enr = enrollments.find(en => en.leaderId === e.target.value);
                                if (enr) handleSetFacilitator(s.id, enr.leaderId, enr.leaderName);
                              }}
                            >
                              <option value="">— Select facilitator —</option>
                              {enrollments.filter(e => e.sessionId === s.id).map(e => (
                                <option key={e.leaderId} value={e.leaderId}>{e.leaderName}</option>
                              ))}
                            </select>
                            {s.facilitatorId && (
                              <span className="text-xs text-emerald-600 flex-shrink-0 flex items-center gap-1">
                                <span className="material-symbols-rounded" style={{ fontSize: 14 }}>check_circle</span>
                                Set
                              </span>
                            )}
                          </div>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1">
                          This leader can add live notes and end the session. Defaults to session creator.
                        </p>
                      </div>

                      {/* Enrollments */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Enrolled Leaders
                            {enrollments.filter(e => e.sessionId === s.id).length > 0 && (
                              <span className="ml-1.5 bg-[#011132]/10 text-[#011132] text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                {enrollments.filter(e => e.sessionId === s.id).length}
                              </span>
                            )}
                          </p>
                          <button
                            onClick={openEnrollModal}
                            className="inline-flex items-center gap-1.5 bg-[#011132] text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#0a2254] transition-colors"
                          >
                            <span className="material-symbols-rounded" style={{ fontSize: 14 }}>person_add</span>
                            Enroll Leaders
                          </button>
                        </div>

                        {enrollments.filter(e => e.sessionId === s.id).length === 0 ? (
                          <div className="border border-dashed border-gray-200 rounded-xl py-6 text-center">
                            <span className="material-symbols-rounded text-gray-300 block mb-2" style={{ fontSize: 28 }}>group_add</span>
                            <p className="text-sm font-medium text-gray-500 mb-1">No leaders enrolled yet</p>
                            <p className="text-xs text-gray-500 mb-3">Add leaders to start the review process</p>
                            <button
                              onClick={openEnrollModal}
                              className="inline-flex items-center gap-1.5 bg-[#011132] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#0a2254] transition-colors"
                            >
                              <span className="material-symbols-rounded" style={{ fontSize: 14 }}>person_add</span>
                              Enroll Leaders
                            </button>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-50 rounded-lg border border-gray-100 overflow-hidden">
                            <div className="grid grid-cols-5 gap-2 px-3 py-2 bg-gray-50 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                              <span className="col-span-2">Leader</span>
                              <span className="text-center">KR %</span>
                              <span className="text-center">Self</span>
                              <span className="text-center">Peers</span>
                            </div>
                            {enrollments.filter(e => e.sessionId === s.id).map(e => (
                              <div key={e.id} className="grid grid-cols-5 gap-2 px-3 py-2.5 items-center">
                                <div className="col-span-2">
                                  <p className="text-sm font-semibold text-gray-900 truncate">{e.leaderName}</p>
                                  <p className="text-xs text-gray-500 truncate">{e.leaderJobTitle}</p>
                                </div>
                                <div className="text-center">
                                  <span className={`text-xs font-bold ${e.krDeliveryPct >= 80 ? 'text-emerald-600' : e.krDeliveryPct >= 50 ? 'text-amber-500' : 'text-red-400'}`}>
                                    {e.krDeliveryPct}%
                                  </span>
                                </div>
                                <div className="text-center">
                                  {e.selfSubmitted
                                    ? <span className="material-symbols-rounded text-emerald-500" style={{ fontSize: 16 }}>check_circle</span>
                                    : <span className="material-symbols-rounded text-gray-300" style={{ fontSize: 16 }}>radio_button_unchecked</span>
                                  }
                                </div>
                                <div className="flex items-center justify-center gap-1">
                                  <span className="text-xs text-gray-600">{e.peerSubmittedCount}/{e.totalPeerCount}</span>
                                  <button
                                    onClick={() => handleRefreshKRs(e)}
                                    title="Refresh KR data"
                                    className="text-gray-400 hover:text-gray-700 ml-1"
                                  >
                                    <span className="material-symbols-rounded" style={{ fontSize: 13 }}>refresh</span>
                                  </button>
                                  <button
                                    onClick={() => handleRemoveEnrollment(e.id, e.leaderName)}
                                    title="Remove from session"
                                    className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded p-0.5 ml-0.5 transition-colors"
                                  >
                                    <span className="material-symbols-rounded" style={{ fontSize: 14 }}>person_remove</span>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </div>}
        </section>
      )}

      {/* ═══ CREATE SESSION MODAL ═══ */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Create Leader EPR Session</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Session Name</label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Period</label>
                  <select
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                    value={form.period}
                    onChange={e => setForm(f => ({ ...f, period: e.target.value as CyclePeriod }))}
                  >
                    {['H1', 'H2', 'Q1', 'Q2', 'Q3', 'Q4', 'annual'].map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Year</label>
                  <input
                    type="number"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                    value={form.year}
                    onChange={e => setForm(f => ({ ...f, year: Number(e.target.value) }))}
                  />
                </div>
              </div>

              {/* Auto-trigger dates */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Schedule &amp; Deadlines</p>
                  <span className="text-[10px] text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full font-medium">Auto-advance enabled</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">
                      Self-Review Opens
                      <span className="ml-1 text-blue-500 font-normal">(auto)</span>
                    </label>
                    <input type="date" className="w-full border border-blue-200 bg-blue-50/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" value={form.selfReviewStartDate} onChange={e => setForm(f => ({ ...f, selfReviewStartDate: e.target.value }))} />
                    <p className="text-[10px] text-gray-400 mt-0.5">Session auto-opens on this date</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">
                      Self-Review Closes
                      <span className="ml-1 text-blue-500 font-normal">(auto)</span>
                    </label>
                    <input type="date" className="w-full border border-blue-200 bg-blue-50/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" value={form.selfReviewDeadline} onChange={e => setForm(f => ({ ...f, selfReviewDeadline: e.target.value }))} />
                    <p className="text-[10px] text-gray-400 mt-0.5">Opens peer review if not all done</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">
                      Peer Review Closes
                      <span className="ml-1 text-blue-500 font-normal">(auto)</span>
                    </label>
                    <input type="date" className="w-full border border-blue-200 bg-blue-50/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" value={form.peerReviewDeadline} onChange={e => setForm(f => ({ ...f, peerReviewDeadline: e.target.value }))} />
                    <p className="text-[10px] text-gray-400 mt-0.5">Moves to live session stage</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Live Session Date</label>
                    <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20" value={form.liveSessionDate} onChange={e => setForm(f => ({ ...f, liveSessionDate: e.target.value }))} />
                    <p className="text-[10px] text-gray-400 mt-0.5">Manual — facilitator ends session</p>
                  </div>
                </div>
              </div>

              {/* Score weights */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
                  Score Formula
                  {!weightsValid && <span className="text-red-500 ml-2 normal-case font-normal">Must total 100%</span>}
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {(['peer', 'self', 'live'] as const).map(key => (
                    <div key={key}>
                      <label className="text-xs text-gray-500 capitalize block mb-1">{key}</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number" min={0} max={100} step={5}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center"
                          value={Math.round(form.scoreWeights[key] * 100)}
                          onChange={e => setForm(f => ({
                            ...f,
                            scoreWeights: { ...f.scoreWeights, [key]: Number(e.target.value) / 100 },
                          }))}
                        />
                        <span className="text-xs text-gray-500">%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className={`h-1 rounded-full flex-1 overflow-hidden bg-gray-100`}>
                    <div className="h-full bg-[#011132] rounded-full transition-all" style={{ width: `${Math.min((form.scoreWeights.peer + form.scoreWeights.self + form.scoreWeights.live) * 100, 100)}%` }} />
                  </div>
                  <span className={`text-xs font-medium ${weightsValid ? 'text-emerald-600' : 'text-red-500'}`}>
                    {Math.round((form.scoreWeights.peer + form.scoreWeights.self + form.scoreWeights.live) * 100)}%
                  </span>
                </div>
              </div>

              {/* Template selector */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                  Muscle Template
                </label>
                <select
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                  value={form.templateId}
                  onChange={e => setForm(f => ({ ...f, templateId: e.target.value }))}
                >
                  <option value="">Default — H1 2026 Leadership Manifesto (8 muscles)</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setShowCreateModal(false)} className="text-sm text-gray-500 px-4 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              <button
                onClick={handleCreate}
                disabled={!form.name.trim() || !weightsValid}
                className="bg-[#011132] text-white text-sm font-medium px-5 py-2 rounded-lg hover:bg-[#0a2254] disabled:opacity-50 transition-colors"
              >
                Create Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ENROLL LEADERS MODAL — user picker from OKR Tracker ═══ */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[85vh]">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-base font-bold text-gray-900">Enroll Leaders</h2>
                <p className="text-xs text-gray-500 mt-0.5">Select from users tagged as Leader in OKR Tracker</p>
              </div>
              <button onClick={() => { setShowEnrollModal(false); setSelectedUids(new Set()); }} className="text-gray-400 hover:text-gray-600">
                <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {leaderUsersLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-5 h-5 border-2 border-[#011132] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : leaderUsers.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No users with isLeader=true found in OKR Tracker.</p>
              ) : (
                <div className="space-y-1.5">
                  {leaderUsers.map(u => {
                    const alreadyIn = enrollments.some(e => e.leaderId === u.uid);
                    const checked   = selectedUids.has(u.uid);
                    return (
                      <label
                        key={u.uid}
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                          alreadyIn
                            ? 'bg-gray-50 border-gray-100 opacity-50 cursor-not-allowed'
                            : checked
                            ? 'bg-[#011132]/5 border-[#011132]/30'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={alreadyIn}
                          checked={alreadyIn || checked}
                          onChange={() => {
                            if (alreadyIn) return;
                            setSelectedUids(prev => {
                              const next = new Set(prev);
                              next.has(u.uid) ? next.delete(u.uid) : next.add(u.uid);
                              return next;
                            });
                          }}
                          className="w-4 h-4 accent-[#011132]"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{u.name}</p>
                          <p className="text-xs text-gray-500 truncate">{u.jobTitle || u.email}</p>
                        </div>
                        {alreadyIn && (
                          <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex-shrink-0">Enrolled</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
              <span className="text-xs text-gray-500">
                {selectedUids.size > 0 ? `${selectedUids.size} selected` : 'Select leaders to enroll'}
              </span>
              <div className="flex gap-3">
                <button onClick={() => { setShowEnrollModal(false); setSelectedUids(new Set()); }} className="text-sm text-gray-500 px-4 py-2 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={handleEnroll}
                  disabled={enrolling || selectedUids.size === 0}
                  className="bg-[#011132] text-white text-sm font-medium px-5 py-2 rounded-lg hover:bg-[#0a2254] disabled:opacity-50 transition-colors"
                >
                  {enrolling ? 'Enrolling…' : `Enroll ${selectedUids.size > 0 ? selectedUids.size : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TEMPLATE EDITOR MODAL ═══ */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  {editingTemplate ? 'Edit Template' : 'New Muscle Template'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Define observable behaviour anchors for each muscle</p>
              </div>
              <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Template meta */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Template Name</label>
                  <input
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                    value={tmplName}
                    onChange={e => setTmplName(e.target.value)}
                    placeholder="e.g. H2 2026 Leadership Review"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Description (optional)</label>
                  <input
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                    value={tmplDesc}
                    onChange={e => setTmplDesc(e.target.value)}
                    placeholder="Brief note about this template"
                  />
                </div>
              </div>

              {/* Muscle editors */}
              <div className="space-y-4">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Leadership Muscles ({tmplMuscles.length})
                </p>
                {tmplMuscles.map((m, idx) => (
                  <div key={m.key} className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                    {/* Name + shortDesc */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">Name</label>
                        <input
                          className="w-full border border-gray-200 bg-white rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                          value={m.name}
                          onChange={e => updateMuscleField(idx, 'name', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">Short Description</label>
                        <input
                          className="w-full border border-gray-200 bg-white rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                          value={m.shortDesc}
                          onChange={e => updateMuscleField(idx, 'shortDesc', e.target.value)}
                        />
                      </div>
                    </div>
                    {/* Reflection question */}
                    <div>
                      <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">Self-Reflection Question</label>
                      <input
                        className="w-full border border-gray-200 bg-white rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
                        value={m.question}
                        onChange={e => updateMuscleField(idx, 'question', e.target.value)}
                      />
                    </div>
                    {/* Behaviour anchors */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide block mb-1">✓ What Good Looks Like</label>
                        <textarea
                          rows={3}
                          className="w-full border border-emerald-200 bg-white rounded-lg px-3 py-1.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          value={m.goodLooksLike}
                          onChange={e => updateMuscleField(idx, 'goodLooksLike', e.target.value)}
                          placeholder="Observable strong behaviour…"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-red-500 uppercase tracking-wide block mb-1">✗ What Weak Looks Like</label>
                        <textarea
                          rows={3}
                          className="w-full border border-red-200 bg-white rounded-lg px-3 py-1.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-200"
                          value={m.weakLooksLike}
                          onChange={e => updateMuscleField(idx, 'weakLooksLike', e.target.value)}
                          placeholder="Observable weak behaviour…"
                        />
                      </div>
                    </div>
                    {/* Rubric 1-5 */}
                    <div>
                      <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Scoring Rubric (1–5)</label>
                      <div className="space-y-1.5">
                        {[1,2,3,4,5].map(score => (
                          <div key={score} className="flex items-center gap-2">
                            <span className={`text-xs font-bold w-5 text-center flex-shrink-0 ${score === 5 ? 'text-emerald-600' : score === 1 ? 'text-red-400' : 'text-gray-500'}`}>{score}</span>
                            <input
                              className="flex-1 border border-gray-200 bg-white rounded-lg px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#011132]/20"
                              value={m.rubric[score] ?? ''}
                              onChange={e => updateRubric(idx, score, e.target.value)}
                              placeholder={`Score ${score} description…`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center flex-shrink-0">
              <p className="text-xs text-gray-500">Scoring reflects behaviour & impact — not confidence, style, or visibility.</p>
              <div className="flex gap-3">
                <button onClick={() => setShowTemplateModal(false)} className="text-sm text-gray-500 px-4 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={savingTemplate || !tmplName.trim()}
                  className="bg-[#011132] text-white text-sm font-medium px-5 py-2 rounded-lg hover:bg-[#0a2254] disabled:opacity-50 transition-colors"
                >
                  {savingTemplate ? 'Saving…' : editingTemplate ? 'Save Changes' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderReviewPage;
